import { UserCard } from '@/entities/user'
import { IUser } from '@/entities/user/model/types'
import useRequest from '@/shared/lib/use-request'
import { ConfigStore, LangStore } from '@/shared/model/store'
import { Input, ScrollArea, useDebounce } from '@/shared/ui'
import { RefreshCw, XCircleIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

type Props = {
	onSelect: (user: IUser | null) => void
	selected: IUser | null
}

const SearchUserComponent = ({ onSelect, selected }: Props) => {
	const { fetch: fetchSearchUser, isLoading, dropState } = useRequest<IUser[]>('searchUser')
	const { fetch, isLoading: isSearchLoading, dropState: dropStateUser } = useRequest<IUser>('getUser')

	const { domain } = ConfigStore()
	const [searchValue, setSearchValue] = useState('')
	const [userList, setUserList] = useState<IUser[]>([])
	const { langPack } = LangStore()
	const debouncedSearchValue = useDebounce(searchValue, 500)

	const onSelectHandler = (e, user: IUser) => {
		e.preventDefault()
		onSelect(user)
		setSearchValue('')
		dropState()
		dropStateUser()
	}

	const isLoadingState = isSearchLoading || isLoading

	useEffect(() => {
		if (debouncedSearchValue.startsWith('0x')) {
			fetch({
				params: {
					domain,
					user_addr: debouncedSearchValue
				},
				onSuccess: async res => {
					setUserList([res])
				}
			})
		} else {
			fetchSearchUser({
				params: { domain, query: debouncedSearchValue },
				onSuccess(res) {
					setUserList(res)
				}
			})
		}
	}, [debouncedSearchValue, domain])

	return (
		<div className='relative flex w-full'>
			{isLoadingState && !selected && (
				<div className='absolute right-3 top-3'>
					<RefreshCw className='h-4 w-4 animate-spin' />
				</div>
			)}
			{selected ? (
				<div className='flex w-full items-center justify-between'>
					<UserCard user={selected} />
					<XCircleIcon color='grey' onClick={() => onSelect(null)} className='cursor-pointer' />
				</div>
			) : (
				<Input
					value={searchValue}
					onChange={e => setSearchValue(e.target.value)}
					placeholder={langPack('default.specify_user')}
				/>
			)}
			{userList && !selected && userList.length ? (
				<div className='absolute top-12 z-50 max-h-[245px] w-full overflow-hidden rounded-lg bg-background p-2 shadow-xl'>
					<ScrollArea className='flex max-h-[245px] flex-col gap-4 overflow-y-auto'>
						{userList?.map((user: IUser) => (
							<div key={user.address}>
								<UserCard user={user} onClick={e => onSelectHandler(e, user)} />
							</div>
						))}
					</ScrollArea>
				</div>
			) : null}
		</div>
	)
}

export default SearchUserComponent
